import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CountryModel, CurrencyModel, IncoTermModel, ShipmentModel } from '@app/data-layer/models/common';
import { EntrySummaryModel, InvoiceModel } from '@app/data-layer/models/entry-summaries';
import { CodeRepository, CurrenciesRepository, InvoicesRepository } from '@app/data-layer/repositories';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { CustomValidator } from '@helpers/custom-validator';
import { DateHelper } from '@helpers/date.helper';
import { FormHelper } from '@helpers/form.helper';
import { UtilsHelper } from '@helpers/utils-helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { TranslateService } from '@ngx-translate/core';
import { EntrySummaryStateService, EsOcrQuickFixService } from '@shared/es-mutual/services';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { Observable, ReplaySubject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-es-ocr-quick-fix-invoice-main-details',
  templateUrl: './es-ocr-quick-fix-invoice-main-details.component.html',
  styleUrls: ['./es-ocr-quick-fix-invoice-main-details.component.scss'],
})
export class EsOcrQuickFixInvoiceMainDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;

  @Input() public shipmentId: number;
  @Input() public invoiceMainDetailsFields: ISectionFormField[];

  @Input() public invoiceId: number;

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public entrySummary: EntrySummaryModel;
  public shipment: ShipmentModel;
  public invoice: InvoiceModel;
  public formGroup: UntypedFormGroup;
  public displayErrors: Map<string, string> = new Map<string, string>();
  public formErrors: { [name: string]: IErrorForm } = {};
  public invoiceMainDetailsDisplayFields: ISectionFormField[] = [];
  public allInvoiceNumbers: string[];

  public isShowClearFormButton: boolean = false;
  public isCurrencyChanged: boolean = false;
  public isWarningCurrencyModalOpen: boolean = false;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private codeRepository: CodeRepository,
    private translateService: TranslateService,
    private invoicesRepository: InvoicesRepository,
    private currenciesRepository: CurrenciesRepository,
    private esOcrQuickFixService: EsOcrQuickFixService,
    private entrySummaryStateService: EntrySummaryStateService,
  ) {}

  ngOnInit(): void {
    this.entrySummary = this.esOcrQuickFixService.getEntrySummary();
    this.shipment = this.entrySummary?.shipments.find((shipment) => shipment.id === this.shipmentId);
    this.invoice = this.shipment.invoices.find((invoice) => invoice.id === this.invoiceId);
    this.allInvoiceNumbers = this.getInvoiceNumbers();

    this.initializeFormGroup();

    this.setFieldsProperties();

    this.populateDisplayErrors();
    this.createFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onSubmit(needValidate: boolean = true): void {
    if (needValidate && this.formGroup.invalid) {
      const invalidFields = this.fh.validateAllFields(this.formGroup);
      this.missingFieldsModal.openCustomModal(invalidFields);
      return;
    }

    if (needValidate && this.isCurrencyChanged) {
      this.setIsWarningCurrencyModalOpen(true);
      this.isCurrencyChanged = false;
      return;
    }

    this.setIsWarningCurrencyModalOpen(false);

    this.updateInvoice()
      .pipe(
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber)),
        takeUntil(this.destroy$),
      )
      .subscribe((entry) => {
        if (!!entry) {
          this.entrySummaryStateService.updateCurrentEntrySummary(entry);
          this.dataSaved.emit();
        }
      });
  }

  public isAnyFormBlockFieldEmpty(blockFields: ISectionFormField[]): boolean {
    let invalidFields = blockFields.find((field) => this.formGroup.get(field.name)?.invalid);
    return !!invalidFields;
  }

  public setIsWarningCurrencyModalOpen(value: boolean) {
    this.isWarningCurrencyModalOpen = value;
  }

  public resetCurrency(): void {
    this.formGroup.get('currency').patchValue(this.invoice.currency || DEFAULT_CURRENCY_CODE);
    this.setIsWarningCurrencyModalOpen(false);
  }

  public clearForm(): void {
    this.formGroup.get('invoiceNumber').reset();
    this.formGroup.get('currency').reset('USD');
    this.formGroup.get('incoTermsCode').reset();
    this.formGroup.get('countryOfExportId').reset();
    this.formGroup.get('countryOfExport').reset();
  }

  public onBlockEdit(isShowEditBlock: boolean) {
    this.isShowClearFormButton = isShowEditBlock;
  }

  private initializeFormGroup() {
    this.formGroup = this.fb.group({
      invoiceNumber: new UntypedFormControl(null, [Validators.required]),
      currency: new UntypedFormControl(DEFAULT_CURRENCY_CODE, [Validators.required]),
      incoTermsCode: new UntypedFormControl(null),
      countryOfExportId: new UntypedFormControl(null, [Validators.required]),
      countryOfExport: new UntypedFormControl(null),
    });

    const currency = this.invoice?.currency || DEFAULT_CURRENCY_CODE;
    this.formGroup.patchValue({
      ...this.invoice,
      currency,
    });

    this.formGroup.get('invoiceNumber').setValidators([
      Validators.required,
      Validators.maxLength(17),
      CustomValidator.notMatchValues(
        this.allInvoiceNumbers.filter((item: string) => item !== this.invoice?.invoiceNumber),
        'invoiceNumberMatch',
      ),
    ]);

    this.formGroup.controls.currency.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.isCurrencyChanged = this.invoice.currency !== value;

      this.syncCurrencyRateChange();
    });

    if (this.formGroup.invalid) {
      this.formGroup.markAsDirty();
      this.formGroup.markAllAsTouched();
    }
  }

  private setFieldsProperties() {
    const incotermsLabel = this.translateService.instant('LABELS.INCOTERMS');
    const optionalLabel = this.translateService.instant('FORM_LABEL.OPTIONAL');

    this.invoiceMainDetailsFields.forEach((field) => {
      switch (field.name) {
        case 'currency':
          if (this.formGroup.get('currency').value !== DEFAULT_CURRENCY_CODE) {
            const setCurrencyRate = (currencyRate: number) => {
              field.comparisonValue = currencyRate;
              this.cdr.detectChanges();
            };

            this.getCurrencyRate(setCurrencyRate);
          } else {
            field.comparisonValue = null;
          }

          const setCurrencyItems = (currencies: CurrencyModel[]) => {
            field.items = currencies;
          };

          this.getCurrencies(setCurrencyItems);
          break;
        case 'incoTermsCode':
          const incotermsLabel = this.translateService.instant('LABELS.INCOTERMS');
          const optionalLabel = this.translateService.instant('FORM_LABEL.OPTIONAL');
          field.label = `${incotermsLabel} ${optionalLabel}`;

          const setIncoTermsItems = (incoterms: IncoTermModel[]) => {
            field.items = incoterms;
            field.searchFn = this.searchInvoice;
          };

          this.getIncoterms(setIncoTermsItems);
          break;
        case 'countryOfExportId':
          field.change = this.getCountries.bind(this);

          const setCountryOfExportInitItems = (country: CountryModel) => {
            field.initializedItems = [country];
          };

          this.getInitCountryOfExport(setCountryOfExportInitItems);
          break;
        default:
          break;
      }
    });

    const isInvoiceCountryOfExportExist = !!this.invoice?.countryOfExport;

    this.invoiceMainDetailsDisplayFields.push(
      {
        type: 'text',
        label: 'LABELS.INVOICE_NUMBER',
        name: 'invoiceNumber',
        value: this.invoice?.invoiceNumber,
      },
      {
        type: 'select',
        label: 'LABELS.CURRENCY',
        name: 'currency',
        value: this.invoice?.currency,
      },
      {
        type: 'select',
        label: `${incotermsLabel} ${optionalLabel}`,
        name: 'incoTermsCode',
        value: this.invoice?.incoTermsCode,
      },
      {
        type: 'select',
        label: 'LABELS.COUNTRY_OF_EXPORT',
        name: 'countryOfExportId',
        value: isInvoiceCountryOfExportExist ? `${this.invoice?.countryOfExport?.code} ${this.invoice?.countryOfExport?.description}` : '',
      },
    );
  }

  private populateDisplayErrors() {
    Object.keys(this.formGroup.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'invoiceNumber':
        case 'currency':
        case 'countryOfExportId':
          this.displayErrors.set(fieldName, this.fieldMissingErrorText(fieldName));
          break;
        default:
          break;
      }
    });
  }

  private createFormErrors(): void {
    this.invoiceMainDetailsFields.forEach((field) => {
      this.formErrors[field.name] = {
        required: 'FORM_ERROR.REQUIRED',
        invoiceNumberMatch: 'FORM_ERROR.INVOICE_NUMBER_MATCH',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
        currencyIsNotAvailable: 'BACK_ERROR.CURRENCY_IS_NOT_AVAILABLE',
      };
    });
  }

  private fieldMissingErrorText(fieldName: string | string[]): string {
    return !this.formGroup.get(fieldName)?.value ? 'LABELS.MISSING' : '';
  }

  private syncCurrencyRateChange() {
    this.invoiceMainDetailsFields.forEach((field) => {
      switch (field.name) {
        case 'currency':
          const currency = this.formGroup.get('currency').value;
          if (!!currency && currency !== DEFAULT_CURRENCY_CODE) {
            const setCurrencyRate = (currencyRate: number) => {
              field.comparisonValue = currencyRate;
              this.cdr.detectChanges();
            };

            this.getCurrencyRate(setCurrencyRate);
          } else {
            field.comparisonValue = null;
          }
          break;

        default:
          break;
      }
    });
  }
  private getCurrencies(callback?: (currencies: CurrencyModel[]) => void) {
    this.currenciesRepository
      .getCurrencies()
      .pipe(takeUntil(this.destroy$))
      .subscribe((currencies) => {
        callback(currencies);
      });
  }

  private updateInvoice(newInvoiceData?: Partial<InvoiceModel>) {
    if (!newInvoiceData) {
      newInvoiceData = {
        ...this.formGroup.value,
        manufacturerId: this.invoice?.manufacturerId,
        manufacturer: this.invoice?.manufacturer,
      };
    }

    return this.invoicesRepository.update(this.invoice.id, newInvoiceData);
  }

  private getInvoiceNumbers(): string[] {
    const result: string[] = [];

    if (this.entrySummary.shipments && this.entrySummary.shipments.length) {
      for (const shipment of this.entrySummary.shipments) {
        result.push(...shipment.invoices.map((invoice: InvoiceModel) => invoice.invoiceNumber));
      }
    }

    return UtilsHelper.uniq<string>(result);
  }

  private searchInvoice(term: string, item: IncoTermModel) {
    term = term.toLowerCase().trim();
    return item.code.toLowerCase().indexOf(term) > -1 || item.name.toLowerCase().indexOf(term) > -1;
  }

  private getCurrencyRate(callback?: (currencyRate: number) => void) {
    const exportDate = this.shipment?.exportDate ? this.shipment.exportDate : DateHelper.now;
    const currency = this.formGroup.get('currency').value;

    this.currenciesRepository
      .getRate(currency, exportDate)
      .pipe(take(1))
      .subscribe((currencyRate) => {
        callback(currencyRate ? currencyRate.rate : NaN);
      });
  }

  private getIncoterms(callback?: (incoterms: IncoTermModel[]) => void) {
    this.codeRepository
      .getIncoterms()
      .pipe(take(1))
      .subscribe((incoterms) => {
        callback(incoterms);
        this.cdr.detectChanges();
      });
  }

  private getInitCountryOfExport(callback?: (country: CountryModel) => void): void {
    if (!this.formGroup.get('countryOfExportId').value || !this.formGroup.get('countryOfExport')?.value?.code) {
      return;
    }

    this.codeRepository
      .getCountry(this.formGroup.get('countryOfExport')?.value?.code)
      .pipe(takeUntil(this.destroy$))
      .subscribe((country: CountryModel) => {
        callback(country);
      });
  }

  private getCountries(code: string): Observable<CountryModel[]> {
    return this.codeRepository.searchCountry(code);
  }
}
